import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";

const PartnerFormPopup = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const redirectTimerRef = React.useRef(null);

    const [formData, setFormData] = useState({
        name: "",
        company: "",
        countryCode: "",
        phone: "",
        email: "",
        partnerType: "",
        otherPartnerType: "",
        email2: "",
        additionalInfo: "",
    });

    const [errors, setErrors] = useState({});
    const [activeErrorField, setActiveErrorField] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [isOtherCountryCode, setIsOtherCountryCode] = useState(false);
    const [isOtherPartnerType, setIsOtherPartnerType] = useState(false);

    const countryOptions = [
        { label: "United States (+1)", value: "+1" },
        { label: "Canada (+1)", value: "+1" },
        { label: "India (+91)", value: "+91" },
        { label: "United Kingdom (+44)", value: "+44" },
        { label: "Australia (+61)", value: "+61" },
    ];

    const FIELD_ORDER = ["name", "company", "email", "countryCode", "phone", "partnerType", "otherPartnerType"];

    const inputToErrorKey = (inputName) => {
        if (inputName === "countryCodeDropdown") return "countryCode";
        return inputName;
    };

    const handleClose = () => {
        if (redirectTimerRef.current) {
            clearTimeout(redirectTimerRef.current);
            redirectTimerRef.current = null;
        }
        if (success) {
            navigate("/");
        }
        onClose();
    };

    useEffect(() => {
        return () => {
            if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
        };
    }, []);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape") handleClose();
        };

        if (isOpen) {
            document.body.style.overflow = "hidden";
            window.addEventListener("keydown", handleEscape);
        } else {
            document.body.style.overflow = "auto";
            setFormData({
                name: "",
                company: "",
                countryCode: "",
                phone: "",
                email: "",
                partnerType: "",
                otherPartnerType: "",
                email2: "",
                additionalInfo: "",
            });
            setErrors({});
            setActiveErrorField(null);
            setIsOtherCountryCode(false);
            setIsOtherPartnerType(false);
            setSuccess(false);
        }
        return () => {
            document.body.style.overflow = "auto";
            window.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, onClose, success]);

    // ── Validation ────────────────────────────────────────────────────────────
    const validateForm = (data, otherCountryCode, otherPartnerType) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const newErrors = {};

        if (!data.name.trim()) {
            newErrors.name = "Full name is required.";
        }

        if (!data.company.trim()) {
            newErrors.company = "Company / Brand name is required.";
        }

        if (!data.email.trim()) {
            newErrors.email = "Email address is required.";
        } else if (!emailRegex.test(data.email.trim())) {
            newErrors.email = "Please enter a valid email address.";
        }

        // Country code
        if (!data.countryCode || data.countryCode === "+") {
            newErrors.countryCode = "Please select or enter a country code.";
        } else if (otherCountryCode && !/^\+[0-9]+$/.test(data.countryCode)) {
            newErrors.countryCode = "Country code must start with + followed by digits.";
        }

        // Phone
        const strippedPhone = data.phone.replace(/\D/g, "");
        if (!strippedPhone) {
            newErrors.phone = "Phone number is required.";
        } else if (strippedPhone.length < 6 || strippedPhone.length > 15) {
            newErrors.phone = "Please enter between 6 and 15 digits.";
        }

        // Partner type
        if (!data.partnerType) {
            newErrors.partnerType = "Please select a partner type.";
        } else if (data.partnerType === "Other") {
            if (!data.otherPartnerType.trim()) {
                newErrors.otherPartnerType = "Please specify your partner type when selecting 'Other'.";
            }
        }

        return newErrors;
    };

    // ── Input handler ─────────────────────────────────────────────────────────
    const handleInputChange = (e) => {
        const target = e.target;
        const { name, value } = target;

        const thisErrorKey = inputToErrorKey(name);

        let finalValue = value;
        let otherCountryVal = isOtherCountryCode;

        if (name === "countryCodeDropdown") {
            if (value === "Other") {
                setIsOtherCountryCode(true);
                finalValue = "+";
                otherCountryVal = true;
            } else {
                setIsOtherCountryCode(false);
                finalValue = value;
                otherCountryVal = false;
            }
            setErrors((prev) => {
                const updated = { ...prev };
                delete updated.countryCode;
                return updated;
            });
            if (activeErrorField === "countryCode") {
                setActiveErrorField(null);
            }
        } else if (name === "partnerType") {
            setIsOtherPartnerType(value === "Other");
            finalValue = value;
            setErrors((prev) => {
                const updated = { ...prev };
                delete updated.partnerType;
                delete updated.otherPartnerType;
                return updated;
            });
            if (activeErrorField === "partnerType" || activeErrorField === "otherPartnerType") {
                setActiveErrorField(null);
            }
        } else if (name === "countryCode") {
            let val = value;
            if (!val.startsWith("+")) {
                val = "+" + val.replace(/\D/g, "");
            } else {
                val = "+" + val.substring(1).replace(/\D/g, "");
            }
            if (val.length > 5) val = val.substring(0, 5);
            finalValue = val;
        } else if (name === "phone") {
            finalValue = value.replace(/\D/g, "");
        }

        if (errors[thisErrorKey]) {
            const currentErrText = errors[thisErrorKey];
            let newErrorForField = undefined;

            if (thisErrorKey === "name") {
                if (finalValue.trim() !== "") {
                    newErrorForField = null;
                } else {
                    newErrorForField = "Full name is required.";
                }
            } else if (thisErrorKey === "company") {
                if (finalValue.trim() !== "") {
                    newErrorForField = null;
                } else {
                    newErrorForField = "Company / Brand name is required.";
                }
            } else if (thisErrorKey === "email") {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (finalValue.trim() === "") {
                    newErrorForField = "Email address is required.";
                } else {
                    const isValid = emailRegex.test(finalValue.trim());
                    if (isValid) {
                        newErrorForField = null;
                    } else {
                        if (currentErrText === "Email address is required.") {
                            newErrorForField = null;
                        } else {
                            newErrorForField = "Please enter a valid email address.";
                        }
                    }
                }
            } else if (thisErrorKey === "countryCode") {
                if (!finalValue || finalValue === "+") {
                    newErrorForField = "Please select or enter a country code.";
                } else {
                    if (otherCountryVal) {
                        const isValidCC = /^\+[0-9]+$/.test(finalValue);
                        if (isValidCC) {
                            newErrorForField = null;
                        } else {
                            if (currentErrText === "Please select or enter a country code.") {
                                newErrorForField = null;
                            } else {
                                newErrorForField = "Country code must start with + followed by digits.";
                            }
                        }
                    } else {
                        newErrorForField = null;
                    }
                }
            } else if (thisErrorKey === "phone") {
                if (finalValue === "") {
                    newErrorForField = "Phone number is required.";
                } else {
                    const isValidPhone = finalValue.length >= 6 && finalValue.length <= 15;
                    if (isValidPhone) {
                        newErrorForField = null;
                    } else {
                        if (currentErrText === "Phone number is required.") {
                            newErrorForField = null;
                        } else {
                            newErrorForField = "Please enter between 6 and 15 digits.";
                        }
                    }
                }
            } else if (thisErrorKey === "partnerType") {
                if (finalValue !== "") {
                    newErrorForField = null;
                } else {
                    newErrorForField = "Please select a partner type.";
                }
            } else if (thisErrorKey === "otherPartnerType") {
                if (finalValue.trim() !== "") {
                    newErrorForField = null;
                } else {
                    newErrorForField = "Please specify your partner type when selecting 'Other'.";
                }
            }

            if (newErrorForField !== undefined) {
                setErrors((prev) => {
                    const updated = { ...prev };
                    if (newErrorForField === null) {
                        delete updated[thisErrorKey];
                        if (thisErrorKey === "partnerType") delete updated.otherPartnerType;
                        
                        // Clear active error field highlight if we just corrected it
                        if (activeErrorField === thisErrorKey || (thisErrorKey === "partnerType" && activeErrorField === "otherPartnerType")) {
                            setActiveErrorField(null);
                        }
                    } else {
                        updated[thisErrorKey] = newErrorForField;
                    }
                    return updated;
                });
            }
        }

        if (name === "countryCodeDropdown") {
            setFormData((prev) => ({ ...prev, countryCode: finalValue }));
        } else if (name === "partnerType") {
            setFormData((prev) => ({ ...prev, partnerType: finalValue, otherPartnerType: "" }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: finalValue }));
        }
    };

    const resetToDropdown = () => {
        setIsOtherCountryCode(false);
        setFormData((prev) => ({ ...prev, countryCode: "" }));
        setErrors((prev) => { const u = { ...prev }; delete u.countryCode; return u; });
        if (activeErrorField === "countryCode") setActiveErrorField(null);
    };

    const resetToPartnerDropdown = () => {
        setIsOtherPartnerType(false);
        setFormData((prev) => ({ ...prev, partnerType: "", otherPartnerType: "" }));
        setErrors((prev) => { const u = { ...prev }; delete u.partnerType; delete u.otherPartnerType; return u; });
        if (activeErrorField === "partnerType" || activeErrorField === "otherPartnerType") setActiveErrorField(null);
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.email2) return; // Block spam

        const validationErrors = validateForm(formData, isOtherCountryCode, isOtherPartnerType);

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            const firstErr = FIELD_ORDER.find((f) => validationErrors[f]);
            setActiveErrorField(firstErr);

            setTimeout(() => {
                const domName = firstErr === "countryCode" && !isOtherCountryCode ? "countryCodeDropdown" : firstErr;
                const element = document.getElementsByName(domName)[0] || document.querySelector(`[name="${domName}"]`);
                if (element) {
                    element.scrollIntoView({ behavior: "smooth", block: "center" });
                    element.focus({ preventScroll: true });
                }
            }, 50);

            return;
        }

        setErrors({});
        setActiveErrorField(null);
        setIsSubmitting(true);

        try {
            /*const GOOGLE_SCRIPT_URL =
                "https://script.google.com/macros/s/AKfycbwwUyqoqcu9Fw08nnUwgd2E9RjICbPNDgQqczSyXcPRMnZhINa_3KaQR1e-gxqLfn8CUQ/exec";*///Abhya's URL
                const GOOGLE_SCRIPT_URL ="https://script.google.com/macros/s/AKfycbz9l56bh4J3s-oGg9w3ZHE-Ne0iWdsUUH7HDGD6GL0ykr22_PVU1y5JYNiMgHgvoYbl/exec";

            const submissionData = {
                fullName: formData.name,
                company: formData.company,
                email: formData.email,
                countryCode: formData.countryCode === "+" ? "" : formData.countryCode,
                phone: formData.phone,
                partnerType:
                    formData.partnerType === "Other"
                        ? formData.otherPartnerType
                        : formData.partnerType,
                additionalInfo: formData.additionalInfo,
            };

            await fetch(GOOGLE_SCRIPT_URL, {
                method: "POST",
                mode: "no-cors",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(submissionData),
            });

            setSuccess(true);
            redirectTimerRef.current = setTimeout(() => {
                onClose();
                navigate("/");
            }, 6000);
        } catch (error) {
            console.error("Submission error:", error);
            setSuccess(true);
            redirectTimerRef.current = setTimeout(() => {
                onClose();
                navigate("/");
            }, 6000);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const firstErrorField = activeErrorField;

    return ReactDOM.createPortal(
        <div className="modal-overlay">
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                <button className="close-btn" onClick={handleClose}>&times;</button>

                {!success ? (
                    <div className="form-content">
                        <h2 className="modal-title">Distribute Your Content on Fawesome</h2>
                        <p className="modal-subtext">
                            Whether you're an independent creator or an established studio, Fawesome helps you distribute your content across leading OTT and CTV platforms—<strong>at no cost.</strong>
                        </p>
                        <p className="modal-subtext">
                            Reach a global audience, monetize through ads, and let us handle the platform distribution.
                        </p>
                        <p className="modal-instruction">Start your distribution journey by sharing a few details below.</p>

                        <form onSubmit={handleSubmit} className="partner-form" noValidate>
                            <div className="form-grid">
                                {/* Full Name */}
                                <div className="form-group">
                                    <label>Full Name <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className={firstErrorField === "name" ? "error" : ""}
                                    />
                                    {firstErrorField === "name" && <span className="error-text">{errors.name}</span>}
                                </div>

                                {/* Company */}
                                <div className="form-group">
                                    <label>Company / Brand Name <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleInputChange}
                                        className={firstErrorField === "company" ? "error" : ""}
                                    />
                                    {firstErrorField === "company" && <span className="error-text">{errors.company}</span>}
                                </div>

                                {/* Email */}
                                <div className="form-group">
                                    <label>Email Address <span className="required">*</span></label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={firstErrorField === "email" ? "error" : ""}
                                    />
                                    {firstErrorField === "email" && <span className="error-text">{errors.email}</span>}
                                </div>

                                {/* Phone */}
                                <div className="form-group">
                                    <label>Phone Number <span className="required">*</span></label>
                                    <div className="phone-input-group">
                                        {!isOtherCountryCode ? (
                                            <select
                                                name="countryCodeDropdown"
                                                className={`country-code-select${firstErrorField === "countryCode" ? " error" : ""}`}
                                                value={formData.countryCode}
                                                onChange={handleInputChange}
                                            >
                                                <option value={formData.countryCode} hidden>
                                                    {formData.countryCode || "+"}
                                                </option>
                                                {countryOptions.map((opt) => (
                                                    <option key={opt.label} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                                <option value="Other">Other</option>
                                            </select>
                                        ) : (
                                            <div className="manual-entry-wrapper country-code-wrapper">
                                                <input
                                                    type="text"
                                                    name="countryCode"
                                                    className={`country-code-input manual-entry${firstErrorField === "countryCode" ? " error" : ""}`}
                                                    value={formData.countryCode}
                                                    onChange={handleInputChange}
                                                    placeholder="+"
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    onClick={resetToDropdown}
                                                    className="reset-select-btn"
                                                    title="Back to list"
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        )}
                                        <input
                                            type="tel"
                                            name="phone"
                                            className={`phone-main-input${firstErrorField === "phone" ? " error" : ""}`}
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            placeholder="123 456 7890"
                                        />
                                    </div>
                                    {/* Show only the first phone-row error in priority order */}
                                    {firstErrorField === "countryCode" && <span className="error-text">{errors.countryCode}</span>}
                                    {firstErrorField === "phone" && <span className="error-text">{errors.phone}</span>}
                                </div>
                            </div>

                            {/* Partner Type */}
                            <div className="form-group">
                                <label>Partner type: <span className="required">*</span></label>
                                {!isOtherPartnerType ? (
                                    <select
                                        name="partnerType"
                                        value={formData.partnerType}
                                        onChange={handleInputChange}
                                        className={firstErrorField === "partnerType" ? "error" : ""}
                                    >
                                        <option value="">Select an option</option>
                                        <option value="Independent Content Creator">Independent Content Creator</option>
                                        <option value="Filmmaker">Filmmaker</option>
                                        <option value="Content Studio">Content Studio</option>
                                        <option value="Production House">Production House</option>
                                        <option value="Entertainment Company">Entertainment Company</option>
                                        <option value="Other">Other</option>
                                    </select>
                                ) : (
                                    <div className="manual-entry-wrapper partner-type-wrapper">
                                        <input
                                            type="text"
                                            name="otherPartnerType"
                                            className={`partner-type-input manual-entry${firstErrorField === "otherPartnerType" ? " error" : ""}`}
                                            value={formData.otherPartnerType}
                                            onChange={handleInputChange}
                                            placeholder="Please specify"
                                            maxLength="50"
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            onClick={resetToPartnerDropdown}
                                            className="reset-select-btn"
                                            title="Back to list"
                                        >
                                            &times;
                                        </button>
                                    </div>
                                )}
                                {firstErrorField === "partnerType" && <span className="error-text">{errors.partnerType}</span>}
                                {firstErrorField === "otherPartnerType" && <span className="error-text">{errors.otherPartnerType}</span>}
                            </div>

                            {/* Honeypot */}
                            <input
                                type="text"
                                name="email2"
                                value={formData.email2}
                                onChange={handleInputChange}
                                className="honeypot-field"
                                tabIndex="-1"
                                autoComplete="off"
                            />

                            {/* Additional Info */}
                            <div className="form-group full-width">
                                <label>Additional Information (Optional)</label>
                                <textarea
                                    name="additionalInfo"
                                    className="form-textarea"
                                    value={formData.additionalInfo}
                                    onChange={handleInputChange}
                                    placeholder="Tell us more about your content or specific requirements..."
                                    maxLength="500"
                                />
                            </div>

                            <div className="form-footer-note">
                                <p>Trusted by creators worldwide across Roku, Fire TV, Samsung TV Plus, and more.</p>
                            </div>

                            <button type="submit" className="btn btn-primary submit-btn" disabled={isSubmitting}>
                                {isSubmitting ? "Submitting..." : (
                                    <>
                                        Submit
                                        <img
                                            src="/assets/images/ico_start_arrow.svg"
                                            className="img-fluid ms-2 popup-cta-icon"
                                            alt="icon"
                                        />
                                    </>
                                )}
                            </button>

                            <p className="post-submit-note">
                                Our content team will review your submission and reach out within <strong>3–5 business days</strong>
                            </p>
                        </form>
                    </div>
                ) : (
                    <div className="success-message">
                        <div className="success-icon">✓</div>
                        <h2>Thank You!</h2>
                        <p>Your submission has been received. Our content team will reach out to you within <strong>3–5 business days</strong>.</p>
                        <button className="btn btn-primary" onClick={handleClose}>Close</button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default PartnerFormPopup;